/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext} from 'react';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {setComponentStyle, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {DropdownSelectorDescriptor, DropdownSelectorOption} from './dropdown-selector-control.types';
import styles from './dropdown-selector-control.module.scss';

interface DropdownSelectorControlInnerProps extends EmailMakerControlProps {
  descriptor: DropdownSelectorDescriptor;
}

function DropdownSelectorControlInner({component, descriptor}: DropdownSelectorControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  const rawValue = useStyleProperty(editor, component, descriptor.property);
  const value = rawValue !== '' ? rawValue : descriptor.defaultValue;

  function handleSelect(id?: string): void {
    if (!editor || !id) {
      return;
    }
    setComponentStyle(editor, {
      property: descriptor.property,
      value: id,
      component,
    });
    if (descriptor.onDropDownItemChange) {
      descriptor.onDropDownItemChange(component, descriptor.property, id, editor);
    }
  }

  function getListItems(): ControlledListItemType[] {
    return descriptor.options.map((option) => ({
      id: option.value,
      text: window.i18next.t(option.textKey),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: option.value === value,
      customElement: renderOptionIcon(option),
      customElementClasses: styles.optionIconContainer,
    }));
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelRow}>
        <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} className={styles.label} />
        <Dropdown
          selector={renderTrigger(value, descriptor.options, descriptor.labelKey)}
          popup={<ControlledList items={getListItems()} onClick={handleSelect} />}
          alignment={ALIGNMENT_TYPE.BOTTOM_END}
          popUpHasCustomWidth
          popupClassName={styles.popup}
          doCloseOnInnerClick
        />
      </div>
    </div>
  );
}

function renderOptionImage(option: DropdownSelectorOption, baseClass: string): ReactElement | null {
  if (!option.icon) {
    return null;
  }
  const className = `${baseClass}${option.iconNeedsContrastBackground ? ` ${styles.iconContrastBackground}` : ''}`;
  return <img alt={window.i18next.t(option.textKey)} className={className} src={option.icon} />;
}

function renderOptionIcon(option: DropdownSelectorOption): ReactElement | undefined {
  const img = renderOptionImage(option, styles.optionIcon);
  return img ?? undefined;
}

function renderSelectedIcon(option: DropdownSelectorOption | undefined): ReactElement | null {
  if (!option) {
    return null;
  }
  return renderOptionImage(option, styles.selectedIcon);
}

function renderTrigger(value: string, options: DropdownSelectorDescriptor['options'], labelKey: string): ReactElement {
  const selected = options.find((o) => o.value === value);
  const selectedText = selected ? window.i18next.t(selected.textKey) : value;
  return (
    <button aria-label={window.i18next.t(labelKey)} className={styles.trigger} type="button">
      {renderSelectedIcon(selected)}
      <Text val={selectedText} size={TextSize.MD} className={styles.triggerLabel} />
      <Icon aria-hidden className={styles.triggerChevron} icon="icon-caret-down" size={IconSize.SIZE_ICON_20} />
    </button>
  );
}

export function makeDropdownSelectorControl(descriptor: DropdownSelectorDescriptor): EmailMakerControl {
  return createDescriptorControl(DropdownSelectorControlInner, descriptor);
}
